// Saved-views dropdown for cross-project / personal task surfaces (P1.7).
// Lists the user's saved views for a scope, applies one on click, saves the
// current ViewState as a new named view, and deletes. Owner-private via
// /api/me/views. Self-contained: fetches on mount + after mutations.

import { useEffect, useRef, useState } from "preact/hooks";
import { Bookmark, ChevronDown, Plus, Trash2 } from "lucide-preact";
import { coerceViewState } from "../lib/viewStateStore";
import type { ViewState } from "../lib/viewState";
import {
  createPersonalView,
  deletePersonalView,
  listPersonalViews,
  type PersonalView,
} from "../store/personalViews";
import { toastError, toastSuccess } from "../lib/toast";

interface Props {
  scopeKey: string;
  /** Current view state — saved verbatim when the user creates a view. */
  current: ViewState;
  /** Apply a chosen view's state. */
  onApply: (state: ViewState) => void;
}

export function SavedViewsMenu(props: Props): preact.JSX.Element {
  const { scopeKey, current, onApply } = props;
  const [views, setViews] = useState<PersonalView[]>([]);
  const [open, setOpen] = useState(false);
  const [naming, setNaming] = useState(false);
  const [name, setName] = useState("");
  const ref = useRef<HTMLDivElement>(null);
  // Getrennt von `views`: das GET wurde bisher komplett stumm geschluckt
  // (kein Toast — api() toastet nur Schreibfehler — und kein eigener
  // Zustand), sodass ein Fehlschlag genauso aussah wie "keine gespeicherten
  // Ansichten". Wer wirklich welche hatte, sah sie einfach nicht mehr.
  const [loadFailed, setLoadFailed] = useState(false);

  const refresh = (): void => {
    void listPersonalViews(scopeKey).then(
      (rows) => {
        setLoadFailed(false);
        setViews(rows ?? []);
      },
      () => setLoadFailed(true)
    );
  };

  useEffect(refresh, [scopeKey]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent): void => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const save = async (): Promise<void> => {
    const trimmed = name.trim();
    if (!trimmed) return;
    try {
      await createPersonalView(scopeKey, trimmed, current);
      setName("");
      setNaming(false);
      toastSuccess(`Ansicht „${trimmed}" gespeichert`);
      refresh();
    } catch {
      toastError("Ansicht konnte nicht gespeichert werden");
    }
  };

  const remove = async (id: number): Promise<void> => {
    try {
      await deletePersonalView(id);
      refresh();
    } catch {
      toastError("Ansicht konnte nicht gelöscht werden");
    }
  };

  return (
    <div class="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        class="inline-flex items-center gap-1.5 rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] px-2.5 py-1 text-xs font-medium text-[color:var(--color-on-surface-variant)] hover:bg-[color:var(--color-surface-container-low)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] min-h-[28px]"
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <Bookmark size={14} aria-hidden="true" />
        Ansichten
        {views.length > 0 && (
          <span class="inline-flex items-center justify-center rounded-full bg-[color:var(--color-primary)] text-[color:var(--color-on-primary)] text-label font-semibold min-w-[16px] h-[16px] px-1">
            {views.length}
          </span>
        )}
        <ChevronDown size={14} aria-hidden="true" />
      </button>
      {open && (
        <div role="menu" class="absolute right-0 top-full mt-1 z-20 w-60 max-w-[calc(100vw-1rem)] rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] shadow-level-2 p-1.5 flex flex-col gap-0.5">
          {loadFailed && views.length === 0 ? (
            <p class="px-2 py-1.5 text-micro text-[color:var(--color-error)]">Ansichten konnten nicht geladen werden</p>
          ) : views.length === 0 ? (
            <p class="px-2 py-1.5 text-micro text-[color:var(--color-on-surface-variant)] italic">Noch keine gespeicherten Ansichten</p>
          ) : (
            views.map((v) => (
              <div key={v.id} class="flex items-center gap-1 group">
                <button
                  type="button"
                  onClick={() => {
                    onApply(coerceViewState(v.spec));
                    setOpen(false);
                  }}
                  class="flex-1 min-w-0 truncate text-left rounded px-2 py-1.5 text-xs text-[color:var(--color-on-surface)] hover:bg-[color:var(--color-surface-container-low)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)]"
                >
                  {v.name}
                </button>
                <button
                  type="button"
                  onClick={() => void remove(v.id)}
                  class="shrink-0 inline-flex items-center justify-center size-6 rounded text-[color:var(--color-on-surface-variant)] opacity-100 md:opacity-0 md:group-hover:opacity-100 hover:text-[color:var(--color-error)] focus:outline-none focus:opacity-100"
                  aria-label={`Ansicht „${v.name}" löschen`}
                >
                  <Trash2 size={14} aria-hidden="true" />
                </button>
              </div>
            ))
          )}
          <div class="border-t border-[color:var(--color-outline-variant)] mt-1 pt-1">
            {naming ? (
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  void save();
                }}
                class="flex items-center gap-1"
              >
                <input
                  type="text"
                  value={name}
                  onInput={(e) => setName((e.target as HTMLInputElement).value)}
                  placeholder="Name der Ansicht…"
                  aria-label="Name der Ansicht"
                  maxLength={200}
                  // eslint-disable-next-line jsx-a11y/no-autofocus
                  autofocus
                  class="flex-1 min-w-0 rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] px-2 py-1 text-base sm:text-xs focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)]"
                />
                <button type="submit" disabled={!name.trim()} class="rounded bg-[color:var(--color-primary)] px-2 py-1 text-micro font-medium text-[color:var(--color-on-primary)] disabled:opacity-50">
                  OK
                </button>
              </form>
            ) : (
              <button
                type="button"
                onClick={() => setNaming(true)}
                class="w-full inline-flex items-center gap-1.5 rounded px-2 py-1.5 text-xs text-[color:var(--color-primary-strong)] hover:bg-[color:var(--color-surface-container-low)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)]"
              >
                <Plus size={14} aria-hidden="true" />
                Aktuelle Ansicht speichern
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
